import React, { useEffect, useState } from "react";
import * as yup from "yup";
import ModalInput from "../../ui/ModalTextInput";
import Button from "../../ui/Button";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import Dropdown from "../../ui/SelectDropdown";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  editCourier,
  fetchAllCouriers,
  fetchCourier,
} from "../../../redux/features/CourrierSlice";
import { toast } from "react-toastify";

interface EditCourierCompanyProps {
  open: boolean;
  onClose: () => void;
  courierId?: string;
  page: number;
  limit: number;
}

const EditCourierCompany: React.FC<EditCourierCompanyProps> = ({
  open,
  onClose,
  courierId,
  page,
  limit,
}) => {
  const { loading, currentCourier } = useAppSelector((state) => state.courier);
  const dispatch = useAppDispatch();
  const [formValues, setFormValues] = useState({
    name: currentCourier?.data?.name,
    phoneNumber: currentCourier?.data?.phoneNumber,
    email: currentCourier?.data?.email,
    status: currentCourier?.data?.status,
  });

  const [errors, setErrors] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    status: "",
  });

  const statusOptions = [
    { label: "Active", value: "ACTIVE" },
    { label: "Disabled", value: "DISABLED" },
  ];

  // Yup validation schema
  const validationSchema = yup.object().shape({
    name: yup.string().required("Company name is required"),
    phoneNumber: yup
      .string()
      .matches(/^\d+$/, "Phone number must contain only digits")
      .required("Phone number is required"),
    email: yup
      .string()
      .email("Invalid email Editress")
      .required("Email is required"),
    status: yup.string().required("Status is required"),
  });

  const handleInputChange = (field: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleValidation = async () => {
    try {
      await validationSchema.validate(formValues, { abortEarly: false });
      setErrors({
        name: "",
        phoneNumber: "",
        email: "",
        status: "",
      });
      return true;
    } catch (err: any) {
      const newErrors: any = {};
      err.inner.forEach((error: any) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await handleValidation();
    if (isValid) {
      dispatch(editCourier({ courierId, currierDetails: formValues }))
        .unwrap()
        .then(() => {
          toast.success("Courier edited successfully");
          dispatch(fetchAllCouriers({ page, limit }));
          onClose(); // Close the modal on success
        })
        .catch((error) => {
          console.error("Failed to Edit courier:", error);
          // Handle error (e.g., show a toast message or error notification)
        });
    }
  };

  useEffect(() => {
    dispatch(fetchCourier(courierId));
  }, [courierId, dispatch]);

  useEffect(() => {
    setFormValues({
      name: currentCourier?.data?.name,
      phoneNumber: currentCourier?.data?.phoneNumber,
      email: currentCourier?.data?.email,
      status: currentCourier?.data?.status,
    });
  }, [currentCourier]);

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit Courier Company"
      footer={
        <div className="flex justify-between relative bottom-0">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            layout="inline" variant="ghost"
          />
          <Button
            label="Done"
            type="button"
            onClick={handleSubmit}
            layout="inline"
            isLoading={loading}
          />
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <div className="p-6">
        <ModalInput
          label="Company Name"
          placeholder="Enter company name"
          value={formValues.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          error={errors.name}
          required
        />
        <ModalInput
          label="Phone Number"
          placeholder="Enter phone number"
          value={formValues.phoneNumber}
          onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
          error={errors.phoneNumber}
          required
        />
        <ModalInput
          label="Email Editress"
          type="email"
          placeholder="Enter email Editress"
          value={formValues.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          error={errors.email}
          required
        />
        <Dropdown
          label="Status"
          options={statusOptions}
          value={formValues.status}
          onSelect={(value) => handleInputChange("status", value)}
          error={errors.status}
          placeholder="Select status"
          required
        />
      </div>
    </CustomModalContainer>
  );
};

export default EditCourierCompany;
